import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { SetCurrentUser } from '@taxora-accountant-core/data-access/state/app.actions';
import {
  Api,
  getProfileUsers,
  updateProfile as updateUserProfile,
  updateUser,
  UserEntity,
} from '@taxora-api';
import { ProfilePlanOption, PROFILE_PLAN_OPTIONS } from '@taxora-shared-common/util';
import {
  ChangePassword,
  GetProfile,
  UpdateActivePlan,
  UpdateProfile,
  UpdateProfilePhoto,
} from './profile.actions';

export interface ProfileStateModel {
  isLoading: boolean;
  isChangingPassword: boolean;
  isUpdatingProfile: boolean;
  photoUrl: string | undefined;
  activePlanId: string;
  profile: UserEntity | undefined;
}

@State<ProfileStateModel>({
  name: 'profileState',
  defaults: {
    isLoading: false,
    isChangingPassword: false,
    isUpdatingProfile: false,
    photoUrl: undefined,
    activePlanId: PROFILE_PLAN_OPTIONS[1].id,
    profile: undefined,
  },
})
@Injectable()
export class ProfileState {
  private api = inject(Api);

  @Selector()
  static isLoading(state: ProfileStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static isChangingPassword(state: ProfileStateModel): boolean {
    return state.isChangingPassword;
  }

  @Selector()
  static isUpdatingProfile(state: ProfileStateModel): boolean {
    return state.isUpdatingProfile;
  }

  @Selector()
  static profile(state: ProfileStateModel): UserEntity | undefined {
    return state.profile;
  }

  @Selector()
  static photoUrl(state: ProfileStateModel): string | undefined {
    return state.photoUrl;
  }

  @Selector()
  static availablePlans(): ProfilePlanOption[] {
    return PROFILE_PLAN_OPTIONS;
  }

  @Selector()
  static activePlanId(state: ProfileStateModel): string {
    return state.activePlanId;
  }

  @Selector()
  static activePlan(state: ProfileStateModel): ProfilePlanOption | undefined {
    return PROFILE_PLAN_OPTIONS.find((plan) => plan.id === state.activePlanId);
  }

  @Action(GetProfile)
  async getProfile({ patchState }: StateContext<ProfileStateModel>): Promise<void> {
    patchState({ isLoading: true });

    try {
      const profile = await this.api.invoke(getProfileUsers);

      patchState({
        isLoading: false,
        profile,
      });
    } finally {
      patchState({ isLoading: false });
    }
  }

  @Action(ChangePassword)
  async changePassword(
    { patchState, dispatch }: StateContext<ProfileStateModel>,
    { userId, password }: ChangePassword,
  ): Promise<void> {
    patchState({ isChangingPassword: true });

    try {
      const updatedUser = await this.api.invoke(updateUser, {
        id: userId,
        body: {
          password,
        },
      });

      patchState({
        isChangingPassword: false,
        profile: updatedUser,
      });
      dispatch(new SetCurrentUser(updatedUser));
    } finally {
      patchState({ isChangingPassword: false });
    }
  }

  @Action(UpdateProfile)
  async updateProfile(
    { patchState, dispatch }: StateContext<ProfileStateModel>,
    { payload }: UpdateProfile,
  ): Promise<void> {
    patchState({ isUpdatingProfile: true });

    try {
      const updatedUser = await this.api.invoke(updateUserProfile, {
        body: {
          firstName: payload.firstName,
          lastName: payload.lastName,
          phone: payload.phone.trim(),
        },
      });

      patchState({
        isUpdatingProfile: false,
        profile: updatedUser,
      });
      dispatch(new SetCurrentUser(updatedUser));
    } finally {
      patchState({ isUpdatingProfile: false });
    }
  }

  @Action(UpdateProfilePhoto)
  updateProfilePhoto(
    { patchState }: StateContext<ProfileStateModel>,
    { photoUrl }: UpdateProfilePhoto,
  ): void {
    patchState({ photoUrl });
  }

  @Action(UpdateActivePlan)
  updateActivePlan(
    { patchState }: StateContext<ProfileStateModel>,
    { planId }: UpdateActivePlan,
  ): void {
    patchState({ activePlanId: planId });
  }
}
